import { eventVenueRequirementViolation } from './event-venue';
import { venueGeoRequirementViolation } from './venue-geo';
import { ticketQuotaOverflow } from './ticket-quota';
import { CAPACITY_CEILING_RULES, capacityCeilingViolation } from './capacity-ceiling';
import { wallClockToIso } from './wall-clock';
import { DRAFT_UPLOAD_TTL_MS } from '../constants/upload-ttl';
/**
 * ETKİNLİK SİHİRBAZININ TASLAK MODELİ — tip + yerel kurallar (ui'dan ayrı).
 *
 * `packages/shared` ALTINDADIR çünkü AYNI sihirbaz iki istemcide yaşıyor:
 * web (`create-event-wizard`) ve mobil (`CreateEventScreen`). Kural
 * çoğaltılsaydı iki yüzey sessizce ayrışırdı — CLAUDE.md: "web ve mobil
 * arasında çoğaltılan iş mantığı `packages/shared`'a taşınır". Dosya Blok 32
 * öncesinde `apps/web/src/features/events/model/event-draft.ts` idi; taşındı,
 * kopyalanmadı.
 *
 * Yerel kurallar SUNUCUNUN KURALINI KOPYALAMAZ: mekan zorunluluğu ve kapasite ×
 * bilet tutarlılığı aynı dosyalardan (`event-venue`, `ticket-quota`) okunur,
 * aynı fonksiyon sunucuda da koşar.
 */
export const TOTAL_STEPS = 6;
export const STEP_INDEXES = [1, 2, 3, 4, 5, 6];
export const emptyDraft = {
    step: 1,
    organizationId: '',
    title: '',
    summary: '',
    description: '',
    format: 'in_person',
    startsAt: '',
    endsAt: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Istanbul',
    cityCode: '',
    venueName: '',
    venueAddressLine: '',
    onlineUrl: '',
    capacity: '',
    accessMode: '',
    tickets: [],
    tagIds: [],
    coverMediaId: '',
    coverPreviewUrl: '',
};
/**
 * TASLAKTA GERÇEKTEN DOLDURULMUŞ ALANLAR (defter §15).
 *
 * `step` sayılmaz: hangi adımda kalındığı kullanıcının EMEĞİ değil, gezinme
 * durumudur. Kalan her alan boş taslaktaki değeriyle karşılaştırılır — böylece
 * `format`/`timezone` gibi ÖN DOLU alanlar, kullanıcı onlara dokunmadıkça
 * "dolu" sayılmaz.
 */
export function draftFilledFields(draft) {
    return Object.keys(emptyDraft)
        .filter((key) => key !== 'step')
        .filter((key) => JSON.stringify(draft[key]) !==
        JSON.stringify(emptyDraft[key]));
}
/** Depoya yazmaya değer mi — tek bir alan bile doluysa evet. */
export function draftHasAnyInput(draft) {
    return draftFilledFields(draft).length > 0;
}
/**
 * SÜRESİ DOLAN BAĞLANMAMIŞ KAPAK TASLAKTAN DÜŞER (15.10.2026 · QA P2).
 *
 * Taslağın ömrü son yazmadan işler ve kayar; kapağın sunucudaki ömrü yükleme anından işler.
 * Kapak bu yüzden KENDİ yükleme anıyla ölçülür ve `DRAFT_UPLOAD_TTL_MS` dolunca kimliği ile
 * önizleme adresi (atomik çift) birlikte düşer. Güvenli taraf: yükleme anı yoksa (bu turdan
 * önce yazılmış ya da zarfsız eski taslak), okunamıyorsa ya da gelecekteyse (cihaz saati
 * geri alınmış, yaş ölçülemez) kapak düşer. Kullanıcı kapağı yeniden yükler; silinmiş bir
 * kimliği taşıyan taslak ise önizlemede kırık görsel ve kaydette 404 üretirdi.
 */
export function dropExpiredDraftUpload(draft, now) {
    if (!draft.coverMediaId && !draft.coverPreviewUrl)
        return draft;
    const uploadedAt = draft.coverUploadedAt ? Date.parse(draft.coverUploadedAt) : Number.NaN;
    const age = now - uploadedAt;
    if (Number.isFinite(age) && age >= 0 && age <= DRAFT_UPLOAD_TTL_MS)
        return draft;
    const pruned = { ...draft, coverMediaId: '', coverPreviewUrl: '' };
    delete pruned.coverUploadedAt;
    return pruned;
}
/**
 * GERİ YÜKLENDİĞİ SÖYLENMEYE DEĞER Mİ.
 *
 * `organizationId` HARİÇ tutulur: tek kurumlu hesapta o alanı sihirbaz kendisi
 * dolduruyor. Sayılsaydı "Taslağı temizle" eyleminin hemen ardından kurum geri
 * yazılır ve bir sonraki açılışta kullanıcı, hiç yazmadığı bir taslağın geri
 * yüklendiğini okurdu (md. 7: kolaylık engele dönüşmez).
 */
export function draftHasUserWork(draft) {
    return draftFilledFields(draft).some((field) => field !== 'organizationId');
}
export function buildCreateEventInput(draft) {
    return {
        organizationId: draft.organizationId,
        title: draft.title.trim(),
        summary: draft.summary.trim() || null,
        description: draft.description.trim() || null,
        accessMode: draft.accessMode,
        format: draft.format,
        /*
         * DUVAR SAATİ → ISO, ETKİNLİĞİN DİLİMİNDE (elle test 3 · tarih seçici turu).
         * Eskiden `new Date(value).toISOString()` çağrılıyordu ve bu TARAYICININ
         * dilimini uyguluyordu: İstanbul'dan açılan bir New York etkinliğinde
         * organizatörün yazdığı 09:00, listede 02:00 olarak çıkıyordu. Çevirinin
         * tek yeri burasıdır; seçici duvar saati tutar.
         */
        startsAt: draft.startsAt ? wallClockToIso(draft.startsAt, draft.timezone) : '',
        endsAt: draft.endsAt ? wallClockToIso(draft.endsAt, draft.timezone) : '',
        timezone: draft.timezone,
        venue: draft.format === 'online'
            ? undefined
            : {
                name: draft.venueName.trim() || null,
                addressLine: draft.venueAddressLine.trim() || null,
                cityCode: (draft.cityCode || null),
                ...(draft.venueGeo !== undefined ? { geo: draft.venueGeo } : {}),
            },
        onlineUrl: draft.onlineUrl.trim() || null,
        capacity: draft.capacity ? Number(draft.capacity) : null,
        tagIds: draft.tagIds,
        /* Boş taslak `null` gönderir: sunucuda "kapak yok" ile aynı anlam (B-12). */
        coverMediaId: draft.coverMediaId || null,
        /*
         * BİLETLER GÖVDEYE GİRER (P1). Eskiden 4. adımda tanımlanan türler bu
         * nesneye hiç konmuyordu: kullanıcı formu dolduruyor, "oluştur" diyor ve
         * veri sessizce kayboluyordu (yayında tek bir varsayılan tür doğuyordu).
         *
         * SATIR ELENMEZ: türü seçilmemiş satır burada filtrelenirse aynı kusur
         * (form topluyor, gövde taşımıyor) küçük ölçekte geri gelir. Eksik satırı
         * `draftIssues` ENGELLER; gönderime kadar gelirse sunucu alan hatası döner.
         */
        tickets: draft.tickets.map((ticket) => ({
            kind: ticket.kind,
            quota: ticket.quota.trim() === '' ? null : Number(ticket.quota),
        })),
    };
}
/** Taslaktaki kapasite — boş dize `null` (sınırsız) demektir. */
export function draftCapacity(draft) {
    return draft.capacity.trim() === '' ? null : Number(draft.capacity);
}
/** Bilet kontenjanları — boş dize `null` (sınırsız) demektir. */
export function draftQuotas(draft) {
    return draft.tickets.map((ticket) => (ticket.quota.trim() === '' ? null : Number(ticket.quota)));
}
/**
 * `ceiling`: etkinliğin katılımcı sınırı (`capacityCeilingFromSummary`); `null`
 * sınırsız ya da bilinmiyor demektir ve yerel denetim uydurulmaz (20.10.2026).
 */
export function draftIssues(draft, now = Date.now(), ceiling = null) {
    const issues = [];
    if (!draft.organizationId) {
        issues.push({ step: 1, path: null, messageKey: 'events.organizationRequired' });
    }
    const venue = eventVenueRequirementViolation(draft.format, draft.venueName, draft.venueAddressLine);
    if (venue) {
        issues.push({ step: 2, path: venue.path, messageKey: `validation.${venue.rule}` });
    }
    const startsAtStamp = draft.startsAt
        ? Date.parse(wallClockToIso(draft.startsAt, draft.timezone))
        : Number.NaN;
    if (!Number.isNaN(startsAtStamp) && startsAtStamp <= now) {
        issues.push({ step: 2, path: 'startsAt', messageKey: 'events.pastDateError' });
    }
    if (!draft.accessMode) {
        issues.push({ step: 3, path: null, messageKey: 'events.accessModeRequired' });
    }
    if (draft.tickets.some((ticket) => ticket.kind === '')) {
        issues.push({ step: 4, path: 'tickets', messageKey: 'events.ticketKindRequired' });
    }
    const overflow = ticketQuotaOverflow(draftCapacity(draft), draftQuotas(draft));
    if (overflow) {
        issues.push({
            step: 4,
            path: 'capacity',
            messageKey: 'events.ticketQuotaOverflow',
            params: { total: overflow.total, capacity: overflow.capacity },
        });
    }
    const capacityOverCeiling = capacityCeilingViolation(draftCapacity(draft), ceiling);
    if (capacityOverCeiling) {
        issues.push({
            step: 4,
            path: 'capacity',
            messageKey: `validation.${CAPACITY_CEILING_RULES.capacity}`,
            params: { limit: capacityOverCeiling.limit },
        });
    }
    const quotaOverCeiling = draftQuotas(draft)
        .map((quota) => capacityCeilingViolation(quota, ceiling))
        .find((violation) => violation !== null);
    if (quotaOverCeiling) {
        issues.push({
            step: 4,
            path: 'tickets',
            messageKey: `validation.${CAPACITY_CEILING_RULES.quota}`,
            params: { limit: quotaOverCeiling.limit },
        });
    }
    // Sunucu en az bir ilgi etiketi ister (öneri motoru sinyali).
    if (draft.tagIds.length === 0) {
        issues.push({ step: 5, path: null, messageKey: 'events.tagsRequired' });
    }
    return issues;
}
/**
 * YAYINDA ARANAN KUSURLAR — taslak kurallarına ek olarak konum zorunluluğu
 * (bölüm 11 madde 1). Zorunluluk YALNIZ yayında denetlenir: taslak konumsuz
 * kaydedilir. Kural `venue-geo`dan okunur; anahtarlar sunucunun herkese açık
 * ayarından gelir ve okunamadıysa zorunluluk uygulanmaz (md. 7, fail-open:
 * son karar sunucunundur).
 */
export function draftPublishIssues(draft, switches, now = Date.now(), ceiling = null) {
    const issues = [...draftIssues(draft, now, ceiling)];
    const geo = venueGeoRequirementViolation(draft.format, draft.venueGeo ?? null, switches);
    if (geo) {
        issues.push({ step: 2, path: geo.path, messageKey: `validation.${geo.rule}` });
    }
    return issues.sort((a, b) => a.step - b.step);
}
export function stepTitleKey(step) {
    if (step === 1)
        return 'events.step1Title';
    if (step === 2)
        return 'events.step2Title';
    if (step === 3)
        return 'events.step3Title';
    if (step === 4)
        return 'events.step4Title';
    if (step === 5)
        return 'events.step5Title';
    return 'events.step6Title';
}
export function stepDescKey(step) {
    if (step === 1)
        return 'events.step1Desc';
    if (step === 2)
        return 'events.step2Desc';
    if (step === 3)
        return 'events.step3Desc';
    if (step === 4)
        return 'events.step4Desc';
    if (step === 5)
        return 'events.step5Desc';
    return 'events.step6Desc';
}
export function checklistFor(draft) {
    return [
        {
            id: 'organization',
            labelKey: 'events.checkOrganizationLabel',
            descKey: 'events.checkOrganizationDesc',
            ok: Boolean(draft.organizationId),
            blocking: true,
            step: 1,
        },
        {
            id: 'basics',
            labelKey: 'events.checkBasicsLabel',
            descKey: 'events.checkBasicsDesc',
            ok: draft.title.trim().length > 0 && draft.summary.trim().length > 0,
            step: 1,
        },
        {
            id: 'schedule',
            labelKey: 'events.checkScheduleLabel',
            descKey: 'events.checkScheduleDesc',
            ok: Boolean(draft.startsAt && draft.endsAt && draft.timezone.trim()),
            step: 2,
        },
        {
            /*
             * KONUM SATIRI ADRESİ DE SAYAR (defter §13): yüz yüze/hibrit etkinlikte
             * mekan adı + açık adres zorunlu olduğu için kontrol listesi de onları
             * sorar — aksi halde liste "tamam" derken sunucu reddederdi.
             */
            id: 'place',
            labelKey: 'events.checkPlaceLabel',
            descKey: 'events.checkPlaceDesc',
            ok: draft.format === 'online'
                ? draft.onlineUrl.trim().length > 0
                : draft.cityCode.length > 0 &&
                    draft.venueName.trim().length > 0 &&
                    draft.venueAddressLine.trim().length > 0,
            blocking: draft.format !== 'online',
            step: 2,
        },
        {
            id: 'access',
            labelKey: 'events.checkAccessLabel',
            descKey: 'events.checkAccessDesc',
            ok: Boolean(draft.accessMode),
            blocking: true,
            step: 3,
        },
        {
            id: 'tags',
            labelKey: 'events.checkTagsLabel',
            descKey: 'events.checkTagsDesc',
            ok: draft.tagIds.length > 0,
            blocking: true,
            step: 5,
        },
    ];
}
//# sourceMappingURL=event-draft.js.map